import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Loading from '../../../components/loading';
import ModalErrorMessage from '../../../components/modalErrorMessage/modalErrorMessage';
import { useExpenseCategories } from '../../hooks/useExpenseCategories';
import { useExpenseGoals } from '../hooks/useExpenseGoals';

const TOTAL_PERCENTAGE = 100;

export default function ExpenseGoalsModal({ open, onClose, onSaveSuccess }) {
  const { categories, isLoading: isCategoriesLoading } = useExpenseCategories();
  const { goals, isLoading: isGoalsLoading, saveGoals } = useExpenseGoals();

  const [percentageByCategory, setPercentageByCategory] = useState({});
  const [isSaving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);

  useEffect(() => {
    if (open === false) return;

    const savedPercentages = goals.reduce(
      (accumulated, goal) => ({ ...accumulated, [goal.categoryId]: String(goal.targetPercentage) }),
      {},
    );
    setPercentageByCategory(savedPercentages);
  }, [open, goals]);

  const handlePercentageChange = (categoryId, value) =>
    setPercentageByCategory((previousPercentages) => ({
      ...previousPercentages,
      [categoryId]: value,
    }));

  const totalPercentage = Object.values(percentageByCategory).reduce(
    (accumulated, value) => accumulated + (Number(value) || 0),
    0,
  );

  const handleSave = () => {
    const goalsToSave = Object.entries(percentageByCategory)
      .filter(([, value]) => Number(value) > 0)
      .map(([categoryId, value]) => ({
        categoryId: Number(categoryId),
        targetPercentage: Number(value),
      }));

    setSaving(true);
    saveGoals(goalsToSave)
      .then(() => onSaveSuccess())
      .catch((returnFromApi) => {
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error('ops! ocorreu um erro' + returnFromApi);
      })
      .finally(() => setSaving(false));
  };

  const isLoading = isCategoriesLoading || isGoalsLoading || isSaving;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Loading isToHidden={isLoading === false} />

      <DialogTitle>Metas por categoria</DialogTitle>

      <DialogContent dividers>
        {showErrorMessage && (
          <ModalErrorMessage message={errorMessage} setShow={setShowErrorMessage} />
        )}

        <Typography variant="body2" color="text.secondary" mb={2}>
          Informe quanto da sua receita você quer gastar em cada categoria. Categorias sem
          percentual ficam fora do acompanhamento.
        </Typography>

        <Stack spacing={2}>
          {categories.map((category) => (
            <TextField
              key={category.id}
              label={category.description}
              type="number"
              value={percentageByCategory[category.id] ?? ''}
              onChange={(event) => handlePercentageChange(category.id, event.target.value)}
              slotProps={{
                input: { endAdornment: <InputAdornment position="end">%</InputAdornment> },
                htmlInput: { min: 0, max: 100 },
              }}
            />
          ))}
        </Stack>

        <Alert
          severity={totalPercentage > TOTAL_PERCENTAGE ? 'error' : 'info'}
          variant="outlined"
          sx={{ mt: 2 }}
        >
          Total distribuído: {totalPercentage.toFixed(1)}% da receita
        </Alert>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>

        <Button
          variant="contained"
          disabled={isLoading || totalPercentage > TOTAL_PERCENTAGE}
          onClick={handleSave}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ExpenseGoalsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaveSuccess: PropTypes.func.isRequired,
};
